type NumberOptions = {
	min?: number;
	max?: number;
	precision?: number;
};

const firstNames = [
	'Olivia',
	'Liam',
	'Noah',
	'Emma',
	'Sophia',
	'James',
	'Ava',
	'Lucas',
	'Mia',
	'Ethan',
	'Harper',
	'Elijah',
	'Amelia',
	'Benjamin',
	'Charlotte',
	'Mason',
	'Isabella',
	'Henry',
	'Evelyn',
	'Jack',
];

const lastNames = [
	'Johnson',
	'Williams',
	'Brown',
	'Jones',
	'Garcia',
	'Miller',
	'Davis',
	'Lopez',
	'Wilson',
	'Anderson',
	'Thomas',
	'Taylor',
	'Moore',
	'Jackson',
	'Martin',
];

const domains = [
	'example.com',
	'demo.io',
	'mock.dev',
	'prototype.app',
	'sample.co',
];
const countries = [
	'United States',
	'United Kingdom',
	'Singapore',
	'Canada',
	'Australia',
	'Germany',
	'France',
	'Japan',
	'United Arab Emirates',
	'Netherlands',
];
const stateAbbrs = ['NY', 'CA', 'TX', 'FL', 'WA', 'IL', 'MA', 'CO', 'AZ', 'NJ'];
const cities = [
	'New York',
	'London',
	'Singapore',
	'Toronto',
	'Sydney',
	'Berlin',
	'Paris',
	'Tokyo',
	'Dubai',
	'Amsterdam',
];
const streetNames = [
	'Main',
	'Oak',
	'Park',
	'Maple',
	'Cedar',
	'Elm',
	'King',
	'Queen',
	'River',
	'Lake',
];
const companyWords = [
	'Nova',
	'Atlas',
	'Summit',
	'Vertex',
	'Harbor',
	'Pioneer',
	'Northstar',
	'Bluewave',
	'Sterling',
	'Crest',
];
const companySuffixes = [
	'Holdings',
	'Capital',
	'Advisors',
	'Partners',
	'Group',
	'Limited',
	'Ventures',
	'Analytics',
	'Labs',
	'Systems',
];
const buzzWords = [
	'streamline',
	'accelerate',
	'transform',
	'optimize',
	'secure',
	'orchestrate',
	'enable',
	'scale',
];
const currencyCodes = ['USD', 'EUR', 'GBP', 'SGD', 'AED', 'JPY', 'CAD', 'AUD'];
const hackerPhrases = [
	'reboots the monitoring pipeline',
	'flags suspicious payment behaviour',
	'correlates adverse media findings',
	'updates investigator decision flows',
	'automates watchlist enrichment',
	'evaluates high-risk transaction clusters',
];
const loremSentences = [
	'Additional verification was completed and documented for the alert disposition.',
	'The analyst compared sanctions records against customer KYC documentation.',
	'Multiple identifiers were reviewed before the final decision was recorded.',
	'The workflow remains in prototype mode with static AML reference data.',
	'This sample record demonstrates audit-ready notes for the investigation trail.',
];

let state = 123456789;

function next() {
	state = (state * 1664525 + 1013904223) % 4294967296;
	return state / 4294967296;
}

function randomInt(min: number, max: number) {
	return Math.floor(next() * (max - min + 1)) + min;
}

function sample<T>(items: T[]): T {
	return items[randomInt(0, items.length - 1)];
}

function sampleMany<T>(items: T[], count: number): T[] {
	const pool = [...items];
	const total = Math.min(Math.max(count, 0), pool.length);
	const selected: T[] = [];
	for (let index = 0; index < total; index += 1) {
		const itemIndex = randomInt(0, pool.length - 1);
		selected.push(pool.splice(itemIndex, 1)[0]);
	}
	return selected;
}

function randomNumber(options: NumberOptions = {}) {
	const min = options.min ?? 0;
	const max = options.max ?? 9999;
	const precision = options.precision ?? 1;
	const value = min + next() * (max - min);
	return Math.round(value / precision) * precision;
}

function randomDateBetween(start: number, end: number) {
	return new Date(start + next() * (end - start));
}

const faker = {
	seed(value: number) {
		state = value >>> 0;
	},
	name: {
		findName() {
			return `${sample(firstNames)} ${sample(lastNames)}`;
		},
	},
	internet: {
		email() {
			const first = sample(firstNames).toLowerCase();
			const last = sample(lastNames).toLowerCase();
			return `${first}.${last}@${sample(domains)}`;
		},
	},
	date: {
		past(years = 1) {
			const end = Date.now();
			const start = end - years * 365 * 24 * 60 * 60 * 1000;
			return randomDateBetween(start, end);
		},
		recent(days = 1) {
			const end = Date.now();
			const start = end - days * 24 * 60 * 60 * 1000;
			return randomDateBetween(start, end);
		},
	},
	datatype: {
		uuid() {
			const segment = () => randomInt(0, 0xffff).toString(16).padStart(4, '0');
			return `${segment()}${segment()}-${segment()}-${segment()}-${segment()}-${segment()}${segment()}${segment()}`;
		},
		number(options?: NumberOptions) {
			return randomNumber(options);
		},
	},
	random: {
		arrayElement<T>(items: T[]) {
			return sample(items);
		},
		arrayElements<T>(items: T[], count: number) {
			return sampleMany(items, count);
		},
	},
	address: {
		streetAddress() {
			return `${randomInt(10, 9999)} ${sample(streetNames)} St`;
		},
		city() {
			return sample(cities);
		},
		stateAbbr() {
			return sample(stateAbbrs);
		},
		country() {
			return sample(countries);
		},
	},
	finance: {
		amount(min = 0, max = 1000, decimals = 2) {
			return randomNumber({ min, max, precision: 1 / 10 ** decimals }).toFixed(
				decimals,
			);
		},
		currencyCode() {
			return sample(currencyCodes);
		},
	},
	company: {
		bsBuzz() {
			return sample(buzzWords);
		},
		companyName() {
			return `${sample(companyWords)} ${sample(companySuffixes)}`;
		},
	},
	hacker: {
		phrase() {
			return sample(hackerPhrases);
		},
	},
	lorem: {
		sentence() {
			return sample(loremSentences);
		},
	},
};

export default faker;
